import { A } from "@solidjs/router";
import { createSignal, Show } from "solid-js";
import { createPlaygroundWorkspaces } from "./playgroundQueries";
import { RunCompare } from "./RunCompare";
import { RunHistoryStrip } from "./RunHistoryStrip";
import { RunParamsBar } from "./RunParamsBar";

/** One URL's experiment bench: choose parameters up top, run the agent, and
 * compare two finished runs side by side below. */
export function RunWorkbench(props: { websiteId: string }) {
  const workspaces = createPlaygroundWorkspaces();
  const context = () => {
    for (const workspace of workspaces.data?.workspaces ?? []) {
      const site = workspace.websites.find(
        (candidate) => candidate.websiteId === props.websiteId,
      );
      if (site) return { workspace, site };
    }
    return undefined;
  };

  const [slotA, setSlotA] = createSignal<string | null>(null);
  const [slotB, setSlotB] = createSignal<string | null>(null);

  // First pick fills A, the second B; picking a slotted run clears it, and a
  // third pick replaces A so the newest comparison is always one click away.
  const pick = (runId: string) => {
    if (slotA() === runId) return setSlotA(null);
    if (slotB() === runId) return setSlotB(null);
    if (slotA() === null) return setSlotA(runId);
    if (slotB() === null) return setSlotB(runId);
    setSlotA(runId);
  };

  return (
    <main class="landing">
      <div class="landing-bar">
        <A href="/playground" class="landing-brand">
          ← Playground
        </A>
        <span class="landing-tag" />
      </div>
      <div class="landing-main pg-wide">
        <Show
          when={context()}
          fallback={
            <Show
              when={workspaces.data}
              fallback={<div class="loading">Loading experiment…</div>}
            >
              <div class="empty">
                <div class="empty-title">URL not found</div>
                <A href="/playground">Back to the playground</A>
              </div>
            </Show>
          }
        >
          {(ctx) => (
            <>
              <div class="landing-head-row">
                <div>
                  <A
                    href={`/playground/${ctx().workspace.clientId}`}
                    class="landing-sub"
                  >
                    ← {ctx().workspace.name}
                  </A>
                  <h1 class="landing-h1 mono">{ctx().site.originUrl}</h1>
                  <p class="landing-sub">
                    Run the agent with different instructions or scoring guides,
                    then compare the results.
                  </p>
                </div>
              </div>

              <RunParamsBar
                websiteId={props.websiteId}
                clientId={ctx().workspace.clientId}
                clientName={ctx().workspace.name}
                hasVoice={ctx().workspace.tovVersionId !== null}
              />

              <RunHistoryStrip
                websiteId={props.websiteId}
                slotA={slotA()}
                slotB={slotB()}
                onPick={pick}
              />

              <RunCompare
                websiteId={props.websiteId}
                slotA={slotA()}
                slotB={slotB()}
              />
            </>
          )}
        </Show>
      </div>
    </main>
  );
}
